import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { Badge } from '../../../Components/UI/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../../../Components/UI/tooltip';
import { ArrowLeft, X, Edit2, ChevronRight, LogOut } from 'lucide-react';
import apiService from '../../../Services/APIService';
import { clearEverything } from '../../../Utils/CookieHandler';
import { RiDiscountPercentFill } from 'react-icons/ri';
import useIsMobile from '../../../Hooks/useIsMobile';
import { toast } from 'sonner';
import { createRazorpayOptions, FormatAmount, formatAmount, getCoupons, months, formatDateFull, getAt99 } from '../../../Utils/Utilities';
import { countries } from '../../../Components/Enums/DataEnums';
import { createRazorpayOrderAPI, upgradeSubscriptionAPI, getCouponDetailsAPI } from './UpgradeSubscriptionAPI';
import { formatDateWithTime } from '../../../Utils/DateTime';
import ProfileSection from '../Profile/ProfileSection';
import { getTableDateOrWeekday } from '../../../Utils/DateTime';
import { useSubscriptionModalState } from '../../../Hooks/useSubscriptionModalState';
import { addDays, addMinutes } from 'date-fns';
import CountdownTimer from '../../../Components/Common/CountDownTimer';

const determineDecimalPlaces = (...amounts) => {
  const hasDecimals = amounts.some(amount => {
    const decimalPart = (amount % 1).toFixed(2).slice(2);
    return decimalPart !== '00';
  });

  return hasDecimals ? 2 : 0;
};

const StartSubscriptionDialog = ({
  open,
  onClose,
  loading = false,
  selectedPlan = null,
  selectedBillingPeriod = 'yearly',
  agencyData = null,
  currencySymbol = null,
  initialCouponCode = null,
  quantity = 1, // Number of periods (1, 2, 3, etc.)
  userData = null, // User data for Razorpay prefill
  currentEditionID = 1, // Current subscription edition ID for determining change type
  rfdAvl = false, // Refund available flag
  showLogout = false,
  onPaymentStart = () => { },
  onPaymentCancel = () => { },
  onPaymentSuccess = () => { },
}) => {
  const isIndianUser = Number(agencyData?.CountryID || 1) === 1;
  const finalCurrencySymbol = currencySymbol || (isIndianUser ? '₹' : '$');
  const isMobile = useIsMobile();
  const { setStartSubscriptionModalOpen } = useSubscriptionModalState();

  const handleLogout = async () => {
    try {
      await apiService.accessSetData(2, 'logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearEverything();
      window.location.href = "/";
    }
  };
  const [couponCode, setCouponCode] = useState(initialCouponCode || '');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [showAllCoupons, setShowAllCoupons] = useState(false);
  const [couponSearchQuery, setCouponSearchQuery] = useState('');
  const [appliedCouponType, setAppliedCouponType] = useState('amount'); // 'amount' or 'percentage'
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [appliedCouponData, setAppliedCouponData] = useState(null); // Store full coupon data from API
  const [showTimer, setShowTimer] = useState(true);
  const [isCouponValidatedViaAPI, setIsCouponValidatedViaAPI] = useState(false);

  // Track modal state globally
  useEffect(() => {
    setStartSubscriptionModalOpen(open);
  }, [open, setStartSubscriptionModalOpen]);

  useEffect(() => {
    if (!open || couponCode || !selectedPlan) return;
    if (open && !couponCode && selectedPlan) {
      try {
        const storedCouponData = localStorage.getItem('AppliedCoupon');

        if (storedCouponData) {
          const couponData = JSON.parse(storedCouponData);
          const storedCouponCode = couponData?.couponCode.toUpperCase();

          if (!storedCouponCode) return;

          const pageChangeType = determineChangeType();
          const pageMode = selectedBillingPeriod === 'yearly' ? 'YY' : 'MM';
          const pageEditionID = selectedPlan?.editionID || 1;
          const pageCountryID = agencyData?.CountryID || 1;

          const isMatchingChangeType = couponData.subType === pageChangeType;
          const isMatchingMode = couponData.period === selectedBillingPeriod;

          if (isMatchingChangeType && isMatchingMode) {
            const apiCouponData = {
              couponCode: storedCouponCode.toUpperCase(),
              editionID: pageEditionID,
              upgdType: pageChangeType,
              upgdMode: pageMode,
              upgdQty: quantity,
              countryID: pageCountryID,
              accessType: 'BZPAY'
            };

            handleCouponAPI(
              apiCouponData,
              (discountValue, code, couponDetails) => {
                applyCouponSuccess(discountValue, code, couponDetails);
              },
              (error) => {
                console.warn('Auto-apply coupon failed:', error);
                localStorage.removeItem('AppliedCoupon');
              }
            );
          } else {
            localStorage.removeItem('AppliedCoupon');
          }
        }
      } catch (error) {
        console.warn('Error reading coupon from localStorage:', error);
      }
    }
  }, [open, selectedPlan, selectedBillingPeriod, agencyData, quantity]);

  // Common utility functions
  const determineChangeType = () => {
    // If current subscription is Free (SubTypeID === 1) or no upgrade history, it's a new subscription
    const isNewSubscription = (agencyData?.SubTypeID === 1 || agencyData?.SubTypeID === 0) && !agencyData?.LastUpgdDT;
    if (isNewSubscription) return 'SUB';

    if (selectedPlan?.editionID === currentEditionID) {
      const selectedMode = selectedBillingPeriod === 'yearly' ? 'YY' : 'MM';
      const agencySubMode = agencyData?.SubMode || '';

      if (selectedMode === agencySubMode) {
        return 'RNWL';
      }
      return 'UPGD';
    }

    if (selectedPlan?.editionID > currentEditionID) return 'UPGD';
  };

  const calculateDiscountAmount = () => {
    // const originalPrice = mapPlanDetails().originalPrice;
    const basePrice = mapPlanDetails().price;
    const discount = appliedCouponType === 'percentage'
      ? Math.round((basePrice * appliedDiscount) / 100 * 100) / 100
      : (appliedDiscount);

    return Math.min(discount, basePrice);
  };

  const calculateGSTAfterCoupon = () => {
    // const originalPrice = mapPlanDetails().originalPrice;
    const basePrice = mapPlanDetails().price;
    const discountAmount = calculateDiscountAmount();
    const priceAfterDiscount = Math.max(0, basePrice - discountAmount);
    return Math.round(priceAfterDiscount * 0.18 * 100) / 100;
  };

  const calculateFinalAmount = () => {
    // const originalPrice = mapPlanDetails().originalPrice;
    const basePrice = mapPlanDetails().price;
    const discountAmount = calculateDiscountAmount();
    const priceAfterDiscount = Math.max(0, basePrice - discountAmount);
    const gst = calculateGSTAfterCoupon();
    return Math.round((priceAfterDiscount + gst) * 100) / 100;
  };

  // Common coupon API handler
  const handleCouponAPI = async (couponData, onSuccess, onError) => {
    try {
      const response = await getCouponDetailsAPI(couponData);

      if (response?.error) {
        onError?.(response?.message || 'Invalid coupon code');
        return;
      }

      const couponDataSet = JSON.parse(response?.updatePackage || '[]');

      if (couponDataSet.Table && couponDataSet.Table.length > 0) {
        const couponResponse = couponDataSet.Table[0];
        const couponDetails = couponDataSet.Table1 ? couponDataSet.Table1[0] : null;
        if (Number(couponResponse.FnErrCode) === -1 && couponDataSet.Table1 && couponDataSet.Table1.length > 0) {
          const discountValue = parseFloat(couponDetails.DiscMaxAmt) || 0;
          // const discountType = couponDetails.DiscountType?.toUpperCase() === 'PERCENTAGE' ? 'percentage' : 'amount';
          onSuccess?.(discountValue, couponData.couponCode || couponCode, couponDetails);
        } else {
          onError?.(couponResponse.ErrMsg);
        }
      } else {
        onError?.('Unable to validate coupon. Please try again.');
      }
    } catch (error) {
      onError?.('Invalid coupon code. Please try again.');
    }
  };

  // Common coupon success handler
  const applyCouponSuccess = (discountValue, code, couponDetails = null) => {
    setAppliedDiscount(discountValue);
    setAppliedCouponType('amount');
    setCouponCode(code);
    setAppliedCouponData(couponDetails); // Store full coupon data from API
    setIsCouponValidatedViaAPI(true);
    const discountText = `${finalCurrencySymbol}${discountValue}`;
    localStorage.getItem('AppliedCoupon') && localStorage.removeItem('AppliedCoupon');
    toast.success(`Coupon ${code.toUpperCase()} applied! You saved ${discountText}.`);
  };

  // Common coupon error handler
  const applyCouponError = (errorMessage) => {
    setAppliedDiscount(0);
    setAppliedCouponType('amount');
    setCouponCode('');
    setAppliedCouponData(null); // Clear coupon data on error
    setIsCouponValidatedViaAPI(false);
    toast.error(errorMessage);
  };

  // Common pricing breakdown UI component
  const renderPricingRow = (label, amount, isDiscount = false, className = '', decimalPlaces = 0) => (
    <div className="flex justify-between items-center">
      <span className="text-xs sm:text-sm text-gray-700 font-medium">{label}</span>
      <span className={`text-xs sm:text-sm font-medium ${className}`}>{isDiscount && '-'}<FormatAmount amount={amount} currency={finalCurrencySymbol} showDecimalPoints={decimalPlaces} /></span>
    </div>
  );

  // Common manual coupon input component
  const renderManualCouponInput = (label = "Have a coupon code?") => (
    <div>
      <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-2">
        {label}
      </label>
      <div className="flex gap-2 rounded-lg">
        <input
          type="text"
          placeholder="Enter your coupon code"
          value={couponCode}
          onChange={(e) => {
            setCouponCode(e.target.value);
            if (e.target.value === '') {
              setAppliedDiscount(0);
            }
          }}
          className="flex-1 px-2 sm:px-3 py-2 border border-gray-300 bg-bg-white rounded-md text-xs sm:text-sm"
        />
        <Button
          onClick={() => handleManualCouponApply()}
          className="bg-sahi-primary-color text-white px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg transition-colors flex-shrink-0 !h-8"
        >
          Apply
        </Button>
      </div>
    </div>
  );

  // Common applied coupon display component
  const renderAppliedCouponCard = () => (
    <div className="border rounded-lg border-gray-300 bg-bg-white overflow-hidden">
      <div className="flex flex-col gap-2 p-2 sm:p-3 transition-all bg-bg-white">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <RiDiscountPercentFill className="text-sahi-green text-lg sm:text-xl flex-shrink-0" style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.1))' }} />
            <p className="font-semibold text-sm sm:text-base text-gray-900">Coupon Applied</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-sahi-green-light border border-sahi-green text-sahi-green p-3 py-4 h-8 flex items-center w-fit">
              <span className="text-[14px] font-semibold">{appliedCoupon?.couponCode.toUpperCase() || appliedCouponData?.CouponCode.toUpperCase()}</span>
            </Badge>
          </div>
        </div>
        <p className="text-sm text-gray-600 line-clamp-2">{appliedCoupon?.description || appliedCouponData?.Description}</p>
      </div>
      <div className="border-t-2 border-dashed border-gray-300"></div>
      <button
        onClick={() => setShowAllCoupons(true)}
        className="w-full px-2 py-2 sm:p-3 text-sm font-semibold text-sahi-green hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between gap-2"
      >
        <span>{staticCoupons.length == 0 ? 'Apply Coupon Code' : `View Discount Coupons (${staticCoupons.length})`}</span>
        <ChevronRight size={16} />
      </button>
    </div>
  );

  // Common view discount coupons button
  const renderViewCouponsButton = () => (
    <button
      onClick={() => setShowAllCoupons(true)}
      className="w-full border border-gray-300 mt-3 bg-bg-white text-gray-900 font-semibold py-3 px-2 rounded-lg flex items-center justify-between transition-colors hover:border-gray-400"
    >
      <span className="text-sm sm:text-base flex items-center gap-2">
        <RiDiscountPercentFill className="text-sahi-green text-2xl sm:text-3xl flex-shrink-0" style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.1))' }} />
        <span>View Discount Coupons</span>
        <span className='font-medium'>({staticCoupons.length})</span>
      </span>
      <ChevronRight size={20} />
    </button>
  );

  const changeType = determineChangeType();
  const planMode = selectedBillingPeriod === 'yearly' ? 'YY' : 'MM';

  const couponData = {
    couponCode: couponSearchQuery.trim().toUpperCase(),
    editionID: selectedPlan?.editionID || 1,
    upgdType: changeType,
    upgdMode: planMode,
    upgdQty: quantity,
    countryID: agencyData?.CountryID || 1,
    accessType: 'BZPAY'
  };

  const calculateSubscriptionPeriod = (periodMode = 'YY', quantity = 1, startDate = null) => {
    try {
      const start = startDate ? new Date(startDate) : new Date();

      const end = new Date(start);

      if (periodMode === 'MM') {
        end.setMonth(end.getMonth() + (quantity || 1));
      } else {
        end.setFullYear(end.getFullYear() + (quantity || 1));
      }

      end.setDate(end.getDate() - 1);
      const { startDay, startMonth, startYear } = { startDay: start.getDate(), startMonth: start.getMonth() + 1, startYear: start.getFullYear() };
      const { endDay, endMonth, endYear } = { endDay: end.getDate(), endMonth: end.getMonth() + 1, endYear: end.getFullYear() };

      return {
        startDate: start,
        endDate: end,
        startDateFormatted: getTableDateOrWeekday(start),
        endDateFormatted: getTableDateOrWeekday(end),
        periodText: `${String(startDay).padStart(2, '0')}-${months[startMonth - 1]}-${startYear} to ${String(endDay).padStart(2, '0')}-${months[endMonth - 1]}-${endYear}`
      };
    } catch (error) {
      return {
        startDate: new Date(),
        endDate: new Date(),
        startDateFormatted: 'N/A',
        endDateFormatted: 'N/A',
        periodText: 'N/A'
      };
    }
  };

  const getRefundPeriodExpiry = () => {
    const refundDate = addDays(new Date(), 7);
    const { startDay, startMonth, startYear } = { startDay: refundDate.getDate(), startMonth: refundDate.getMonth() + 1, startYear: refundDate.getFullYear() };
    return `${String(startDay).padStart(2, '0')}-${months[startMonth - 1]}-${startYear}`;
  };

  const calculateCouponExpiry = () => {
    if (!agencyData?.CreatedDT) return null;

    const createdDate = new Date(agencyData.CreatedDT);
    const expiryDate = new Date(createdDate);
    expiryDate.setDate(expiryDate.getDate() + 1);
    expiryDate.setHours(23, 59, 0, 0);

    return expiryDate;
  };

  const isCouponExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date() > expiryDate;
  };
  const couponExpiryDate = calculateCouponExpiry();

  // Static coupons data
  let staticCoupons = getCoupons(selectedPlan?.editionID, selectedBillingPeriod, changeType);

  staticCoupons = staticCoupons.filter(coupon => {
    const byPeriodAndExpiry = coupon.period === selectedBillingPeriod && !isCouponExpired(coupon.validTillDT);

    if (coupon.couponCode === 'FLAT200' && changeType !== 'SUB') {
      return false;
    }

    if (!getAt99 && coupon.proAmt <= 99) {
      return false;
    }

    return byPeriodAndExpiry;
  });

  // Derived state for applied coupon
  const appliedCoupon = staticCoupons.find(c => c.couponCode === couponCode) || null;
  const mapPlanDetails = () => {
    // Handle null or invalid selectedPlan
    if (!selectedPlan || typeof selectedPlan !== 'object') {
      return {
        name: 'ERP+CRM Starter Package',
        description: 'Software Tools to manage Leads, Connections, Quotes, Orders, Billing, Support Ticketing, Inventory, Tasks, Accounts.',
        features: ['10 users', '10 contacts'],
        price: 0,
        originalPrice: 0,
        discount: 0,
        gst: 0,
        totalAmount: 0
      };
    }

    const pricingData = selectedBillingPeriod === 'yearly' ? selectedPlan.yearly : selectedPlan.monthly;

    if (!pricingData || typeof pricingData !== 'object') {
      return {
        name: selectedPlan.name || 'Package',
        description: selectedPlan.description || '',
        features: selectedPlan.features || [],
        price: 0,
        originalPrice: 0,
        discount: 0,
        gst: 0,
        totalAmount: 0
      };
    }

    const isRenewal = changeType === 'RNWL';

    // Get price and originalPrice from pricing data
    let price = parseFloat(pricingData.price) || 0;
    let originalPrice = parseFloat(pricingData.originalPrice) || price;

    if (isRenewal && agencyData) {
      const renewalRate = selectedBillingPeriod === 'yearly' ? agencyData?.AnnRnwlRate : agencyData?.MthRnwlRate;
      if (renewalRate !== undefined && renewalRate !== null && renewalRate !== '' && renewalRate !== 0) {
        price = parseFloat(renewalRate) || price;
        originalPrice = parseFloat(renewalRate) || originalPrice;
      }
    }

    const discountPercent = 0;  //parseFloat(pricingData.discount) || 0;

    // Calculate the final price after quantity
    const finalPrice = price * quantity;

    // Calculate GST (18%) on the final price
    const gst = Math.round(finalPrice * 0.18 * 100) / 100;
    const totalAmount = Math.round((finalPrice + gst) * 100) / 100;

    return {
      name: selectedPlan.name || 'Package',
      description: selectedPlan.description || '',
      features: selectedPlan.features || [],
      price: finalPrice,
      originalPrice: originalPrice * quantity,
      discount: discountPercent,
      gst: gst,
      totalAmount: totalAmount
    };
  };

  const packageDetails = mapPlanDetails();
  const discountAmount = calculateDiscountAmount();
  const gstAfterCoupon = calculateGSTAfterCoupon();
  const finalAmount = calculateFinalAmount();

  // Get company details from agencyData
  const company = agencyData?.AgencyName || '';
  const city = agencyData?.City || '';
  const state = agencyData?.State || '';
  const country = agencyData?.Country || '';
  const taxId = agencyData?.TaxID || '';

  // Build address string (City, State, Country - only add country if not India)
  const buildAddress = () => {
    const addressParts = [];
    if (city) addressParts.push(city);
    if (state) addressParts.push(state);
    if (country && country.toLowerCase() !== 'india') addressParts.push(country);
    return addressParts.join(', ');
  };

  const address = buildAddress();

  const handleClose = () => {
    setCouponCode('');
    setAppliedDiscount(0);
    setAppliedCouponType('amount');
    setAppliedCouponData(null); // Clear coupon data on close
    setIsCouponValidatedViaAPI(false);
    setStartSubscriptionModalOpen(false);
    onClose();
  };

  const isMissingAgencyInfo = !agencyData?.City || !agencyData?.State;

  const handleCloseCouponModal = () => {
    setCouponSearchQuery('');
    setShowAllCoupons(false);
  };

  const handlePayment = async () => {
    setIsPaymentProcessing(true);
    try {
      // Step 1: Validate Razorpay is loaded

      if (isMissingAgencyInfo) {
        toast.error('Please update your billing information to proceed with the payment.');
        setIsPaymentProcessing(false);
        return;
      }

      if (!window.Razorpay) {
        toast.error('Payment gateway not loaded. Please refresh and try again.');
        setIsPaymentProcessing(false);
        return;
      }

      // Step 2: Get plan pricing and mode
      const planPricing = selectedBillingPeriod === 'yearly' ? selectedPlan?.yearly : selectedPlan?.monthly;
      const planMode = selectedBillingPeriod === 'yearly' ? 'YY' : 'MM';

      // Step 3: Create Razorpay order

      const countryObj = countries.find(c => c.CountryID === Number(agencyData?.CountryID || 1));
      const currencyID = agencyData?.CurrencyID || countryObj?.CurrencyID || (isIndianUser ? 24 : 50);  
      const currencyCode = isIndianUser ? 'INR' : 'USD';

      const orderResponse = await createRazorpayOrderAPI({
        amount: finalAmount,
        currencyCode: currencyCode,
        currencyID: currencyID
      });

      if (orderResponse.error) {
        toast.error(orderResponse.message || 'Failed to create order. Please try again.');
        setIsPaymentProcessing(false);
        return;
      }

      const orderDataset = JSON.parse(orderResponse?.updatePackage || '[]');
      if (orderDataset.length === 0) {
        toast.error('Failed to create order. Please try again.');
        setIsPaymentProcessing(false);
        return;
      }

      const orderInfo = orderDataset.Table1[0];

      // Step 4: Prepare upgrade data
      const upgradeData = {
        newEditionID: selectedPlan?.editionID || 1,
        upgdType: changeType,
        upgdQty: quantity,
        upgdMode: planMode,
        countryID: agencyData?.CountryID || 1,
        currencyID: currencyID,
        CurrencyID: currencyID,
        currencyCode: currencyCode,
        currencySymbol: finalCurrencySymbol,
        couponCode: couponCode || '',
        // preTaxAmt: Math.max(0, mapPlanDetails().originalPrice - calculateDiscountAmount()),
        preTaxAmt: Math.max(0, mapPlanDetails().price - calculateDiscountAmount()),
        totalAmt: Math.max(0, finalAmount),
        rzOrderID: orderInfo.rzrpOrderID,
        pmtRef: '',
      };

      // Step 5: Define Razorpay callbacks
      const handleRazorpaySuccess = async (response) => {
        try {
          upgradeData.pmtRef = response.razorpay_payment_id;
          const result = await upgradeSubscriptionAPI(upgradeData);
          if (!result.error) {

            const subnDataSet = JSON.parse(result?.updatePackage || '[]');

            if (subnDataSet.Table && subnDataSet.Table.length > 0) {
              const subnResponse = subnDataSet.Table[0];
              if (Number(subnResponse.FnErrCode) !== -1) {
                toast.error(subnResponse.ErrMsg);
                setIsPaymentProcessing(false);
                return;
              }
            }

            const successMessage = changeType === 'SUB'
              ? 'Your account is activated.'
              : changeType === 'RNWL'
                ? 'Your subscription is renewed.'
                : 'Your account is upgraded.';
            toast.success(successMessage);
            onPaymentSuccess();
          } else {
            toast.error('Failed to process subscription', { description: result.message });
            setIsPaymentProcessing(false);
          }
        } catch (error) {
          toast.error('Error processing subscription. Please try again.');
          setIsPaymentProcessing(false);
        }
      };

      const handleRazorpayDismiss = () => {
        toast.info('Payment cancelled.');
        setIsPaymentProcessing(false);
        onPaymentCancel();
      };

      // Step 6: Create Razorpay options
      const options = createRazorpayOptions(
        agencyData,
        userData,
        handleRazorpaySuccess,
        handleRazorpayDismiss,
        orderInfo
      );

      if (!options) {
        toast.error('Could not prepare payment. Please try again.');
        setIsPaymentProcessing(false);
        return;
      }

      // Step 7: Initialize and open Razorpay
      const razorpay = new window.Razorpay(options);
      handleClose();
      onPaymentStart();
      razorpay.open();

      // Uncomment this section to redirect to external payment gateway instead of Razorpay
      /*
      const upgradeLink = planPricing?.link;
      if (upgradeLink) {
        try {
          handleClose();
          onPaymentClick?.();
          openInExternalBrowser(upgradeLink);
          toast.success('Redirecting to payment gateway...');
          return;
        } catch (error) {
          console.error('Error opening external link:', error);
          toast.error('Could not open payment gateway. Please try again.');
          setIsPaymentProcessing(false);
          return;
        }
      }
      */
    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error('Error initiating payment. Please try again.');
      setIsPaymentProcessing(false);
    }
  };

  const handleApplyCoupon = async (coupon) => {
    const apiCouponData = {
      couponCode: coupon.couponCode,
      editionID: selectedPlan?.editionID || 1,
      upgdType: changeType,
      upgdMode: planMode,
      upgdQty: quantity,
      countryID: agencyData?.CountryID || 1,
      accessType: 'BZPAY'
    };

    await handleCouponAPI(
      apiCouponData,
      (discountValue, code, couponDetails) => {
        applyCouponSuccess(discountValue, code, couponDetails);
        setShowAllCoupons(false);
      },
      applyCouponError
    );
  };

  // Handle manual coupon apply button
  const handleManualCouponApply = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code.');
      return;
    }

    const manualCouponData = {
      couponCode: couponCode.trim().toUpperCase(),
      editionID: selectedPlan?.editionID || 1,
      upgdType: changeType,
      upgdMode: planMode,
      upgdQty: quantity,
      countryID: agencyData?.CountryID || 1,
      accessType: 'BZPAY'
    };

    await handleCouponAPI(manualCouponData, applyCouponSuccess, applyCouponError);
  };

  const subscriptionPeriod = calculateSubscriptionPeriod(
    selectedBillingPeriod === 'yearly' ? 'YY' : 'MM',
    quantity,
    agencyData?.SubTillDT
  );

  // Render package title and description
  const renderPackageTitle = () => {
    // Generate descriptive line from features array
    const generateDescriptiveLine = () => {
      const features = packageDetails.features || [];

      if (features.length === 0) {
        return (
          <>
            {packageDetails.description}
            <br />
            (Comprehensive CRM & ERP tools)
          </>
        );
      }

      // Join features with comma separator, limit to 2-3 features for clean display
      const displayFeatures = features.slice(0, 3).join(', ');
      return (
        <>
          {packageDetails.description}
          <br />
          ({displayFeatures})
        </>
      );
    };

    return (
      <div className="flex-1">
        <h2 className="text-xl sm:text-2xl text-center font-semibold text-gray-900 mb-1">
          {packageDetails.name}
        </h2>
        <p className="text-sm sm:text-base text-center text-gray-600 leading-relaxed">
          {generateDescriptiveLine()}
        </p>
        <p className="text-xs sm:text-sm text-gray-600 font-normal tracking-wide text-center">
          Period: <span className='text-xs sm:text-sm text-gray-700'>{subscriptionPeriod.periodText}</span>
        </p>
      </div>
    );
  };

  // Render company details section (left column)
  const renderCompanyDetails = () => (
    <div className="flex-1">
      {/* Company Details Card */}
      <div className="relative border border-gray-300 rounded-lg bg-bg-white overflow-hidden">

        {/* Edit Button - floating top right */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setShowProfileModal(true)}
              className="absolute top-2 right-2 p-1 text-sahi-primary-color hover:text-sahi-blue transition-colors"
            >
              <Edit2 size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="bg-sahi-primary-color text-white sahi-radius px-2 py-1 text-sm">
            Edit billing information
          </TooltipContent>
        </Tooltip>
        <div className="space-y-2 p-2 sm:p-3">
          {/* Company */}
          {company && (
            <div className="flex items-center gap-2">
              <p className="text-xs sm:text-sm font-semibold text-gray-900">
                {company}
              </p>
            </div>
          )}

          {/* Address */}
          <div className="flex items-center gap-2">
            {!address && <p className="text-xs sm:text-sm text-gray-600 font-normal tracking-wide min-w-fit">
              Location :
            </p>}
            <p className="text-xs sm:text-sm font-normal text-gray-600">
              {address || 'Not Provided'}
            </p>
          </div>

          {/* GSTIN */}
          <div className="flex items-center gap-2">
            <p className="text-xs sm:text-sm text-gray-600 font-normal tracking-wide min-w-fit">
              GSTIN :
            </p>
            <p className="text-xs sm:text-sm font-normal text-gray-600">
              {taxId || 'Not Provided'}
            </p>
          </div>

          {/* Profile completion */}
          {isMissingAgencyInfo ? (
            <span className="text-xs sm:text-sm text-sahi-red">
              Please update your billing information to proceed.
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  // Render pricing breakdown
  const renderPricingBreakdown = () => {
    const showOriginalPrice = packageDetails.discount > 0 && packageDetails.originalPrice > packageDetails.price;
    const showCouponStrikethrough = isCouponValidatedViaAPI && appliedDiscount > 0;
    // const priceAfterDiscount = Math.max(0, packageDetails.originalPrice - discountAmount);
    const priceAfterDiscount = Math.max(0, packageDetails.price - discountAmount);

    const decimalPlaces = determineDecimalPlaces(
      packageDetails.originalPrice,
      isCouponValidatedViaAPI ? discountAmount : 0,
      isCouponValidatedViaAPI ? priceAfterDiscount : packageDetails.price,
      isCouponValidatedViaAPI ? gstAfterCoupon : Math.round(packageDetails.price * 0.18 * 100) / 100,
      isCouponValidatedViaAPI ? finalAmount : Math.round((packageDetails.price + Math.round(packageDetails.price * 0.18 * 100) / 100) * 100) / 100
    );

    const effectiveDiscountAmount = isCouponValidatedViaAPI ? discountAmount : 0;
    const effectivePriceAfterDiscount = Math.max(0, packageDetails.price - effectiveDiscountAmount);
    const effectiveGST = Math.round(effectivePriceAfterDiscount * 0.18 * 100) / 100;
    const effectiveFinalAmount = Math.round((effectivePriceAfterDiscount + effectiveGST) * 100) / 100;

    return (
      <div className="border rounded-lg border-gray-300 bg-bg-white overflow-hidden">
        <div className="space-y-2 p-2 sm:p-3 bg-bg-white">
          {/* Renewal Rate */}
          {renderPricingRow(planMode == 'MM' ? 'Renewal Rate (Monthly)' : 'Renewal Rate (Yearly)', packageDetails.originalPrice, false, 'text-gray-900', decimalPlaces)}

          {/* Applied Coupon Discount - Only show if validated via API */}
          {isCouponValidatedViaAPI && appliedDiscount > 0 && (
            <div className="flex justify-between text-sahi-green">
              <span className="text-xs sm:text-sm font-medium text-gray-700 text-sahi-green">Coupon Discount {appliedCouponType === 'percentage' ? `(${appliedDiscount.toFixed(0)}%)` : ''}</span>
              <span className="text-xs sm:text-sm font-medium flex items-center gap-0">- &nbsp;<FormatAmount amount={effectiveDiscountAmount} currency={finalCurrencySymbol} showDecimalPoints={decimalPlaces} /></span>
            </div>
          )}

          {/* First Year Rate with Strikethroughs */}
          <div className="flex justify-between items-center gap-2">
            <span className="text-xs sm:text-sm text-gray-700 font-medium">{planMode == 'MM' ? 'First Month Rate (Monthly)' : 'First Year Rate (Yearly)'}</span>
            <div className="flex items-center gap-2">
              {showCouponStrikethrough && <span className="font-medium text-gray-500 line-through text-xs"><FormatAmount amount={packageDetails.originalPrice} currency={finalCurrencySymbol} showDecimalPoints={decimalPlaces} /></span>}
              <span className={`text-xs sm:text-sm font-semibold ${showCouponStrikethrough ? 'text-sahi-green font-semibold' : 'text-gray-900'}`}><FormatAmount amount={effectivePriceAfterDiscount} currency={finalCurrencySymbol} showDecimalPoints={decimalPlaces} /></span>
            </div>
          </div>

          {/* GST */}
          {renderPricingRow('GST @ 18%', effectiveGST, false, 'text-gray-900', decimalPlaces)}
        </div>

        <div className="p-2 sm:p-3 border-t border-gray-200 flex items-center bg-bg-white justify-between">
          <span className="text-xs sm:text-sm font-semibold text-gray-900">Total Amount</span>
          <span className="text-base sm:text-lg font-bold text-sahi-green">
            <FormatAmount amount={Math.max(0, effectiveFinalAmount)} currency={finalCurrencySymbol} showDecimalPoints={decimalPlaces} />
          </span>
        </div>
      </div>
    );
  };

  // Render terms and conditions
  const renderTermsAndConditions = () => (
    <div className="px-4 mb-4">
      <p className="text-xs text-muted-foreground text-center">
        By continuing, you agree to the <span className="text-sahi-green cursor-pointer hover:underline">Terms of Service</span> and{' '}
        <span className="text-sahi-green cursor-pointer hover:underline whitespace-nowrap">
          <a href="https://sahicrm.com/Web/policy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </span>. <br />
        GST credit will apply only if GSTIN is valid. This subscription is <span className="whitespace-nowrap">non-refundable{rfdAvl && ` after ${getRefundPeriodExpiry()}`}</span>.
      </p>
    </div>
  );

  // Render payment button
  const renderPaymentButton = () => {
    // Format amount - show decimals only if there are actual decimals
    const formattedAmount = finalAmount % 1 === 0
      ? Math.floor(finalAmount).toLocaleString()
      : finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
      <div className='flex items-center justify-center'>
        <Button
          onClick={handlePayment}
          disabled={isPaymentProcessing || loading}
          className={`${isMobile ? 'w-full' : 'w-5/12'} bg-sahi-primary-color text-white font-semibold rounded-lg transition-colors ${isMobile
            ? 'py-3 sm:py-3 text-base sm:text-lg'
            : 'py-2 sm:py-3 text-sm sm:text-base'
            }`}
        >
          {isPaymentProcessing || loading ? 'Processing...' : <>Pay  <span className="text-md sm:text-base">{finalCurrencySymbol}</span> {formattedAmount}</>}
        </Button>
      </div>
    );
  };

  // Render all coupons modal
  const renderAllCouponsModal = () => {
    const filteredCoupons = staticCoupons.filter(coupon =>
      coupon.couponCode.toLowerCase().includes(couponSearchQuery.toLowerCase()) ||
      coupon.description.toLowerCase().includes(couponSearchQuery.toLowerCase())
    );

    return (
      <Dialog open={showAllCoupons} onOpenChange={(isOpen) => !isOpen && handleCloseCouponModal()}>
        <DialogContent className="w-full min-h-0 max-h-[100vh] lg:max-w-2xl overflow-y-auto">
          <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4 flex items-center justify-between">
            {isMobile && (
              <DialogClose onClick={handleCloseCouponModal} className="p-2 -ml-2">
                <ArrowLeft className="h-6 w-6 text-color" />
                <span className="sr-only">Back</span>
              </DialogClose>
            )}

            <DialogTitle className="text-lg sm:text-xl font-semibold">Apply Coupon</DialogTitle>

            {!isMobile && (
              <DialogClose onClick={handleCloseCouponModal} className="p-2 -mr-2">
                <X className="h-6 w-6 text-color" />
                <span className="sr-only">Close</span>
              </DialogClose>
            )}
          </DialogHeader>

          <div className="space-y-4 sm:space-y-5 px-3 sm:px-4 pb-4">
            {/* Search Coupon Input with Apply Button */}
            <div className="flex gap-2 rounded-lg">
              <input
                type="text"
                placeholder="Enter Coupon Code"
                value={couponSearchQuery}
                onChange={(e) => setCouponSearchQuery(e.target.value)}
                className="flex-1 px-3 sm:px-4 border border-gray-300 bg-white rounded-lg text-sm sm:text-base placeholder-gray-500"
              />
              <Button
                onClick={async () => {
                  if (!couponSearchQuery.trim()) {
                    toast.error('Please enter a coupon code.');
                    return;
                  }

                  const searchCouponData = {
                    couponCode: couponSearchQuery.trim().toUpperCase(),
                    editionID: selectedPlan?.editionID || 1,
                    upgdType: changeType,
                    upgdMode: planMode,
                    upgdQty: quantity,
                    countryID: agencyData?.CountryID || 1,
                    accessType: 'BZPAY'
                  };

                  await handleCouponAPI(
                    searchCouponData,
                    (discountValue, code, couponDetails) => {
                      applyCouponSuccess(discountValue, code, couponDetails);
                      handleCloseCouponModal();
                    },
                    applyCouponError
                  );
                }}
                className="bg-sahi-primary-color text-white px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg transition-colors flex-shrink-0"
              >
                Apply
              </Button>
            </div>

            {/* Available Coupons Section */}
            <div>
              {filteredCoupons.length > 0 && <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Available Coupons</h3>}
              <div className="space-y-3 sm:space-y-4">
                {filteredCoupons.map((coupon) => (
                  <div
                    key={coupon.id}
                    className="p-2 sm:p-3 border border-gray-300 rounded-lg bg-white hover:border-gray-400 transition-all"
                    onClick={() => {
                      handleApplyCoupon(coupon);
                      setShowAllCoupons(false);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2 sm:gap-3">
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <RiDiscountPercentFill className="text-sahi-green text-3xl sm:text-4xl flex-shrink-0 mt-0.5" style={{ filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.1))' }} />
                        <div className="flex-1 min-w-0">
                          {/* Desktop: Badge on same line as coupon code */}
                          <div className="hidden sm:flex items-center justify-between gap-2 flex-wrap">
                            <p className="font-semibold text-sm sm:text-base text-gray-900 truncate">{coupon.couponCode}</p>
                            {coupon.showTimer && coupon.validTillDT && !isCouponExpired(coupon.validTillDT) && showTimer ? (
                              <CountdownTimer
                                expiryDate={coupon?.id == 6 ? coupon.validTillDT : new Date(Date.now() + 24 * 60 * 60 * 1000)}
                                size="sm"
                                showFlash={true}
                                text={coupon?.id == 6 ? "" : "Limited Time Offer!"}
                                onExpire={() => {
                                  setShowTimer(false);
                                }}
                              />
                            ) : coupon.validTillDT && (
                              <div className="group relative">
                                <Badge className="bg-red-100 border border-sahi-red text-sahi-red cursor-pointer">
                                  Validity: {getTableDateOrWeekday(coupon.validTillDT)}
                                </Badge>
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-sahi-primary-color text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                  Coupon valid till: {getTableDateOrWeekday(coupon.validTillDT)}
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="hidden sm:block text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">{coupon.description}</p>
                          {/* Mobile: Badge below coupon code */}
                          <div className="sm:hidden flex flex-col gap-1">
                            <p className="font-semibold text-sm text-gray-900">{coupon.couponCode}</p>
                            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mt-1">{coupon.description}</p>
                            {coupon.showTimer && coupon.validTillDT && !isCouponExpired(coupon.validTillDT) && showTimer ? (
                              <CountdownTimer
                                expiryDate={coupon?.id == 6 ? coupon.validTillDT : new Date(Date.now() + 24 * 60 * 60 * 1000)}
                                size="sm"
                                showFlash={true}
                                text={coupon?.id == 6 ? "" : "Limited Time Offer!"}
                                onExpire={() => {
                                  setShowTimer(false);
                                }}
                              />
                            ) : coupon.validTillDT && (
                              <Badge className="bg-red-100 border border-sahi-red text-sahi-red cursor-pointer w-fit text-xs">
                                Validity: {getTableDateOrWeekday(coupon.validTillDT)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          handleApplyCoupon(coupon);
                          setShowAllCoupons(false);
                        }}
                        className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-md whitespace-nowrap transition-all duration-200 flex-shrink-0 !h-8 ${couponCode === coupon.couponCode
                          ? 'bg-sahi-green text-white shadow-md hover:shadow-lg'
                          : 'bg-gray-200 text-gray-800 hover:shadow-sm'
                          }`}
                      >
                        {couponCode === coupon.couponCode ? '✓ Applied' : 'Apply'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <TooltipProvider>
      <>
        {/* Main Dialog */}
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} preventOutsideClick={true}>
          <DialogContent className="w-full min-h-0 max-h-[100vh] lg:max-w-4xl overflow-y-auto sm:!max-w-[45vw] sm:!w-[45vw]">
            <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4 flex items-center justify-between">
              {isMobile && (
                <DialogClose onClick={handleClose} className="p-2 -ml-2">
                  <ArrowLeft className="h-6 w-6 text-color" />
                  <span className="sr-only">Back</span>
                </DialogClose>
              )}

              <DialogTitle className="text-lg sm:text-xl font-semibold">Start Subscription</DialogTitle>

              <div className="flex items-center gap-2">
                {showLogout && (
                  isMobile ? (
                    <button 
                      onClick={handleLogout} 
                      className="p-2 -mr-2 text-sahi-red hover:bg-sahi-red-light rounded-full transition-colors"
                    >
                      <LogOut className="h-6 w-6" />
                    </button>
                  ) : (
                    <Button
                      onClick={handleLogout}
                      className="bg-sahi-red border-sahi-red hover:text-white hover:border-sahi-red hover:text-sahi-red"
                    >
                      <LogOut className="h-4 w-4" />
                      								Log out
                    </Button>
                  )
                )}
                {!isMobile && (
                  <DialogClose onClick={handleClose} className="p-2 -mr-2">
                    <X className="h-6 w-6 text-color" />
                    <span className="sr-only">Close</span>
                  </DialogClose>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-4 sm:space-y-5 px-3 sm:px-4 pb-4">
              {/* Package Title with Combined Description - Mobile only */}
              {isMobile && (
                <div className="pb-2 sm:pb-3">
                  {renderPackageTitle()}
                </div>
              )}

              {isMobile ? (
                <div className="space-y-4 sm:space-y-5">
                  {/* 1. Pricing Breakdown */}
                  {renderPricingBreakdown()}

                  {(
                  <div className="space-y-2 sm:space-y-2.5">
                    {staticCoupons.length === 0
                      ? renderManualCouponInput()
                      : (appliedCoupon || appliedCouponData)
                        ? renderAppliedCouponCard()
                        : renderViewCouponsButton()
                    }
                  </div>
                  )}

                  {/* 3. Company Details */}
                  {renderCompanyDetails()}

                  {/* 4. Large Pay Button */}
                  <div className="pt-2">
                    {renderPaymentButton()}
                  </div>

                  {/* 5. Terms and Conditions */}
                  {renderTermsAndConditions()}
                </div>
              ) : (
                // Desktop Layout - 50-50 Split Design with Left and Right columns
                <div className="grid grid-cols-2 gap-4 items-start">
                  {/* Selected Package & Billing Information */}
                  <div className="flex flex-col gap-4">
                    {/* Selected Package */}
                    <div className="flex flex-col">
                      <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3 block">
                        Selected Package
                      </label>
                      <div className="border border-gray-300 bg-bg-white rounded-lg overflow-hidden">
                        <div className="space-y-2 p-2 sm:p-3">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                            {packageDetails.name}
                          </h3>
                          <p className="text-xs sm:text-sm text-gray-600 font-normal tracking-wide">
                            Period: <span className='text-xs sm:text-sm text-gray-700'>{subscriptionPeriod.periodText}</span>
                          </p>
                          {packageDetails.features && packageDetails.features.length > 0 && (
                            <div className="space-y-1">
                              {packageDetails.features.map((feature, index) => (
                                <p key={index} className="text-xs sm:text-sm text-gray-700 flex items-center gap-2">
                                  <span className="text-sahi-green">✓</span> {feature}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Billing Information */}
                    <div className="flex flex-col">
                      <div className="flex items-center justify-between mb-2 sm:mb-3">
                        <label className="text-xs sm:text-sm font-semibold text-gray-700">
                          Billing Information
                        </label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => setShowProfileModal(true)}
                              className="p-1 text-sahi-primary-color hover:text-sahi-blue transition-colors"
                            >
                              <Edit2 size={16} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="bg-sahi-primary-color text-white sahi-radius px-2 py-1 text-sm">
                            Edit billing information
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="border border-gray-300 rounded-lg bg-bg-white overflow-hidden">
                        <div className="space-y-2 p-2 sm:p-3">
                          {/* Company */}
                          {company && (
                            <div className="flex items-center gap-2">
                              <p className="text-xs sm:text-sm font-semibold text-gray-900">
                                {company}
                              </p>
                            </div>
                          )}

                          {/* Address */}
                          <div className="flex items-center gap-2">
                            {!address && <p className="text-xs sm:text-sm text-gray-600 font-normal tracking-wide min-w-fit">
                              Location :
                            </p>}
                            <p className="text-xs sm:text-sm font-normal text-gray-600">
                              {address || 'Not Provided'}
                            </p>
                          </div>

                          {/* GSTIN */}
                          <div className="flex items-center gap-2">
                            <p className="text-xs sm:text-sm text-gray-600 font-normal tracking-wide min-w-fit">
                              GSTIN :
                            </p>
                            <p className="text-xs sm:text-sm font-normal text-gray-600">
                              {taxId || 'Not Provided'}
                            </p>
                          </div>

                          {/* Profile Completion */}
                          {(!agencyData?.City || !agencyData?.State) ? (
                            <span className="text-sm text-sahi-red">
                              Please update your billing information to proceed.
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coupons Ribbon/Input & Pricing Breakdown */}
                  <div className="flex flex-col gap-4">
                    {(staticCoupons.length === 0
                      ? renderManualCouponInput()
                      : (appliedCoupon || appliedCouponData)
                        ? (
                          <div className="space-y-2 sm:space-y-2.5">
                            <label className="text-xs sm:text-sm font-semibold text-gray-700 block">
                              &nbsp;
                            </label>
                            {renderAppliedCouponCard()}
                          </div>
                        )
                        : (
                          <div>
                            <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-2">
                              &nbsp;
                            </label>
                            {renderViewCouponsButton()}
                          </div>
                        )
                    )}

                    {/* Bill Summary */}
                    <div className="flex flex-col">
                      <label className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 sm:mb-3 block">
                        Bill Summary
                      </label>
                      {renderPricingBreakdown()}
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Button */}
              {!isMobile && renderPaymentButton()}

              {/* Terms and Conditions */}
              {!isMobile && renderTermsAndConditions()}

            </div>
          </DialogContent>
        </Dialog>

        {renderAllCouponsModal()}

        {/* Profile Modal */}
        {showProfileModal && (
          <ProfileSection
            open={showProfileModal}
            onClose={() => setShowProfileModal(false)}
            requiredFields={true}
            isUpgradeFlow={changeType === 'SUB'} // Passing the prop here

          />
        )}
      </>
    </TooltipProvider>
  );
};

export default StartSubscriptionDialog;